"use client"

/**
 * 04 — Everything lands in one plan, as a two-stage scroll-pinned reveal.
 *
 * Eugene's brief (03.09): first the heading and lede over a SHARP photograph,
 * then, as you keep scrolling, the photograph blurs, a dark scrim fades over
 * it and the collage tiles fly in. The Figma frame is the end state of that
 * second beat, not a static section.
 *
 * Mechanics are lifted from the `/go/*` immersive sections
 * (`immunity-immersive.tsx`), including the two things that make them cheap:
 *
 *   1. The blur is NOT animated. A pre-blurred copy sits underneath and a
 *      sharp copy cross-fades out above it, so no frame ever runs a filter.
 *   2. Scroll progress is written to CSS custom properties on the stage inside
 *      a rAF loop — no React state, so no re-render per frame.
 *
 * Reduced motion jumps straight to the end state and registers no listeners.
 *
 * The tiles themselves are stacks of real layers (`home-v4-plan-tiles.tsx`):
 * the photograph, then DOM on top of it. This file owns the reveal of the
 * BOXES — their scroll-driven fade and stagger — and flips `data-in` on each
 * box once it is about two thirds in, which is what plays the entrance of
 * whatever sits inside it. The two are deliberately separate clocks: a box
 * scrubs with the scrollbar, a message popping up inside it does not.
 *   3. A scroll that goes quiet mid-beat settles that beat on its own
 *      (`onIdle`), so nobody is left looking at half-faded tiles.
 */

import Image from "next/image"
import { blobMediaUrl } from "@/lib/blob-url"
import { useCallback, useEffect, useRef } from "react"
import { useHomeV4Copy } from "./home-v4-copy"
import {
  PlanTileStyles,
  PlanPhoneTile,
  PlanHeroTile,
  PlanChatTile,
  PlanMriTile,
  PlanHandTile,
} from "./home-v4-plan-tiles"
import {
  VpnKeyIcon,
  HealthAndSafetyIcon,
  LockPersonIcon,
} from "./home-v4-figma"

/**
 * The background is a fresh one-pass composite of the male member reading his
 * report at home, regenerated from the original portrait and report sources.
 * Its wide copy-safe left side keeps the heading clear of the member.
 */
const PLAN_BG = blobMediaUrl("/images/marketing/home-v4/plan-demo-report.webp")

const TRUST_ICONS = {
  vpn: VpnKeyIcon,
  shield: HealthAndSafetyIcon,
  lock: LockPersonIcon,
}

/** Where each beat sits along the pinned track, 0→1. */
const CFG = {
  trackVh: 220,
  /** Beat one holds the sharp photo; the blur/darken cross-fade runs here. */
  blurStart: 0.06,
  blurEnd: 0.32,
  /** Beat two: the tiles arrive. */
  revealStart: 0.3,
  revealEnd: 0.85,
  /** How much of the reveal window one tile's own fade takes. */
  tileWindow: 0.45,
  /** Stagger between tiles, in progress units. */
  tileStep: 0.07,
  maxBlur: 18,
  darken: 0.45,
}

/**
 * Time constants, ms, of the ease that carries the stage to its target.
 * `scrub` trails the scrollbar closely enough to feel attached to it while
 * turning each wheel tick into a glide; `settle` finishes a beat the reader
 * left half done; `blur` does the same for beat one alone; `release` hands a
 * settled beat back to the scrollbar.
 */
const TAU = { scrub: 110, settle: 220, blur: 160, release: 90 }

const clamp01 = (v: number) => Math.min(1, Math.max(0, v))

/** The collage box's own aspect ratio, straight from the Figma frame. */
const BOX_W = 1512
const BOX_H = 687

/**
 * Everything above the collage inside the pinned stage: 104 top padding, the
 * 20px eyebrow, its 10px gap, the 112px two-line heading, and the collage
 * area's own 48/40 padding (48 rather than the 24 first tried: the tiles sat
 * on the heading). The lede is absolutely positioned and so counts for
 * nothing here — which is the point.
 */
const STAGE_HEADER = 334

const PLAN_TILE_COMPONENTS = {
  chat: PlanChatTile,
  hero: PlanHeroTile,
  phone: PlanPhoneTile,
  mri: PlanMriTile,
  hand: PlanHandTile,
} as const

export function HomeV4OnePlan({ section }: { section: string }) {
  const trackRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)

  /** What the stage shows right now, so a settle can start from it. */
  const shown = useRef({ t: 0, reveal: 0 })
  /**
   * A beat that has settled on its own stays settled until the reader is back
   * at its start, so the scrubbed and the settled values never fight on the
   * way down.
   */
  const settled = useRef({ blur: false, reveal: false })
  /**
   * Where the stage is heading. `shown` eases towards it every frame, so a
   * wheel tick lands as a short glide rather than a jump: read straight off
   * the scrollbar, the tiles flickered (Eugene, 10.09). `tau` is that ease's
   * time constant — short while the scrollbar leads, longer for a settle.
   */
  const target = useRef({ t: 0, reveal: 0 })
  const tau = useRef(TAU.scrub)
  const loopRaf = useRef(0)
  const lastFrame = useRef(0)
  const idleTimer = useRef(0)

  const render = useCallback((t: number, reveal: number) => {
    const stage = stageRef.current
    if (!stage) return
    shown.current = { t, reveal }
    stage.style.setProperty("--sharp", `${1 - t}`)
    stage.style.setProperty("--dark", `${t}`)
    stage.style.setProperty("--reveal", `${reveal}`)
    // What sits INSIDE a tile plays its entrance (a short rise, the phone
    // riding up) rather than scrubbing with the scrollbar: `data-in` flips
    // once the box is about two thirds in and drops again on the way up, so
    // the layers replay. See `home-v4-plan-tiles.tsx`.
    // With hysteresis: in once the box is 70% faded, out again only below
    // 30%, so a reader parked near the edge does not see the layers flicker
    // in and out (Eugene, 10.09).
    stage.querySelectorAll<HTMLElement>(".hv3-plan-tile").forEach((tile, i) => {
      const start = i * CFG.tileStep
      const edge = tile.hasAttribute("data-in") ? 0.3 : 0.7
      tile.toggleAttribute("data-in", reveal >= start + CFG.tileWindow * edge)
    })
    // The lede belongs to beat one only: it is the sentence that explains the
    // photograph, and once the tiles start arriving they are the explanation.
    // It clears well before the first tile is fully in, so the two never read
    // as competing for the same column.
    stage.style.setProperty("--lede", `${clamp01(1 - reveal / 0.35)}`)
  }, [])

  /** Beat progress straight from the scrollbar, before any settling. */
  const rawProgress = useCallback(() => {
    const track = trackRef.current
    if (!track) return null
    const total = track.offsetHeight - window.innerHeight
    const p =
      total > 0 ? clamp01(-track.getBoundingClientRect().top / total) : 0
    return {
      t: clamp01(
        (p - CFG.blurStart) / Math.max(0.01, CFG.blurEnd - CFG.blurStart)
      ),
      reveal: clamp01(
        (p - CFG.revealStart) / Math.max(0.01, CFG.revealEnd - CFG.revealStart)
      ),
    }
  }, [])

  /** One frame of the ease towards `target`; keeps itself going until it lands. */
  const tick = useCallback(
    (now: number) => {
      const dt = Math.min(64, now - (lastFrame.current || now))
      lastFrame.current = now
      const k = 1 - Math.exp(-dt / tau.current)
      const s = shown.current
      const g = target.current
      let t = s.t + (g.t - s.t) * k
      let reveal = s.reveal + (g.reveal - s.reveal) * k
      const landed =
        Math.abs(g.t - t) < 0.002 && Math.abs(g.reveal - reveal) < 0.002
      if (landed) {
        t = g.t
        reveal = g.reveal
        lastFrame.current = 0
      }
      render(t, reveal)
      loopRaf.current = landed ? 0 : requestAnimationFrame(tick)
    },
    [render]
  )

  /** Point the stage at `g` and ease there with time constant `ms`. */
  const moveTo = useCallback(
    (g: { t: number; reveal: number }, ms: number) => {
      target.current = g
      tau.current = ms
      if (!loopRaf.current) {
        lastFrame.current = 0
        loopRaf.current = requestAnimationFrame(tick)
      }
    },
    [tick]
  )

  const applyFromScroll = useCallback(() => {
    const raw = rawProgress()
    if (!raw) return
    const s = settled.current
    // Back at a beat's start: hand it to the scrollbar again, easing down
    // rather than snapping out of the settled state. Both beats are checked
    // in the same pass — a jump straight to the top releases both at once.
    const released = (s.reveal && raw.reveal < 0.02) || (s.blur && raw.t < 0.02)
    if (s.reveal && raw.reveal < 0.02) s.reveal = false
    if (s.blur && raw.t < 0.02) s.blur = false
    const want = { t: s.blur ? 1 : raw.t, reveal: s.reveal ? 1 : raw.reveal }
    if (released) {
      moveTo(want, TAU.release)
      return
    }
    // A settle owns the stage until it lands.
    if (loopRaf.current && tau.current > TAU.scrub) return
    moveTo(want, TAU.scrub)
  }, [rawProgress, moveTo])

  /**
   * The reveal follows the scrollbar, but a reader who stops a little short
   * must not be left with half-faded tiles (Eugene, 04.09): the moment the
   * scroll goes quiet mid-beat, that beat finishes on its own. Beat one (the
   * blur) settles alone while the tiles have not started; once they have,
   * both land together.
   */
  const onIdle = useCallback(() => {
    const raw = rawProgress()
    if (!raw) return
    const s = settled.current
    if (!s.reveal && raw.reveal > 0 && raw.reveal < 1) {
      s.reveal = true
      s.blur = true
      moveTo({ t: 1, reveal: 1 }, TAU.settle)
      return
    }
    if (!s.blur && raw.t > 0 && raw.t < 1 && raw.reveal === 0) {
      s.blur = true
      moveTo({ t: 1, reveal: target.current.reveal }, TAU.blur)
    }
  }, [rawProgress, moveTo])

  useEffect(() => {
    // Read the query once rather than through a hook: a reduced-motion hook
    // that reports `false` on the first client render would play the whole
    // reveal before correcting itself.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      render(1, 1)
      return
    }
    let raf = 0
    const onScroll = () => {
      if (!raf)
        raf = requestAnimationFrame(() => {
          raf = 0
          applyFromScroll()
        })
      window.clearTimeout(idleTimer.current)
      idleTimer.current = window.setTimeout(onIdle, 220)
    }
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll)
    return () => {
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
      if (raf) cancelAnimationFrame(raf)
      cancelAnimationFrame(loopRaf.current)
      loopRaf.current = 0
      window.clearTimeout(idleTimer.current)
    }
  }, [applyFromScroll, onIdle, render])

  const { ONE_PLAN: COPY } = useHomeV4Copy()
  // Each tile is a stack of real layers now (`home-v4-plan-tiles.tsx`); this
  // only places the boxes. Order is the Figma frame's, back to front.
  const tiles = COPY.tiles.map((tile, i) => {
    const Tile = PLAN_TILE_COMPONENTS[tile.kind]
    return (
      <div
        key={tile.kind}
        className="hv3-plan-tile absolute"
        style={
          {
            left: `${(tile.x / BOX_W) * 100}%`,
            top: `${(tile.y / BOX_H) * 100}%`,
            width: `${(tile.w / BOX_W) * 100}%`,
            aspectRatio: "1 / 1",
            "--i": i,
          } as React.CSSProperties
        }
      >
        <Tile />
      </div>
    )
  })

  const trustRow = COPY.trust.map((item) => {
    const Icon = TRUST_ICONS[item.icon]
    return (
      <li key={item.text} className="flex items-start gap-[11px]">
        <Icon className="size-7 shrink-0 text-white" />
        <p className="hv3-plan-label w-[162px] text-white">
          {" "}
          {/* ds-typo-ignore */}
          {item.text}
        </p>
      </li>
    )
  })

  return (
    <>
      <PlanTileStyles />
      <style>{`
        /* Per-tile progress: the shared --reveal minus this tile's stagger,
           normalised over its own window. Unitless throughout — a clamp() or
           min() that mixes a number with a length is invalid and the whole
           declaration is dropped silently. */
        .hv3-plan-tile{
          --t:clamp(0, calc((var(--reveal, 1) - var(--i) * ${CFG.tileStep}) / ${CFG.tileWindow}), 1);
          opacity:var(--t);
          transform:translateY(calc((1 - var(--t)) * 48px));
          will-change:opacity, transform;
        }
        /* Figma sets this row at 12.5/16.25/0.25 caps — between label-sm (11)
           and label-md (12). Rounding it to a token reflows the three
           fixed-width columns, so it stays off-ladder and page-scoped rather
           than becoming another arbitrary Tailwind value. */
        .hv3-plan-label{font-size:12.5px;line-height:16.25px;letter-spacing:.25px;font-weight:500;text-transform:uppercase}
        .hv3-plan-ground{background-color:#1a1310} /* ds-color-ignore — the design's warm ground under the photograph; DS neutrals are cool greys, so no token matches it */
        .hv3-plan-trust{
          --t:clamp(0, calc((var(--reveal, 1) - 0.45) / 0.4), 1);
          opacity:calc(var(--t) * 0.9);
        }
        @media (prefers-reduced-motion: reduce){
          .hv3-plan-tile{opacity:1;transform:none}
          .hv3-plan-trust{opacity:.9}
        }
      `}</style>

      {/* Desktop: pinned two-beat reveal. The track is full-bleed the way the
          /go immersive sections are — the stage is a viewport, not a column. */}
      <div
        ref={trackRef}
        className="relative hidden w-screen lg:block"
        style={{
          height: `${CFG.trackVh}vh`,
          marginLeft: "calc(50% - 50vw)",
          marginRight: "calc(50% - 50vw)",
        }}
      >
        <div
          ref={stageRef}
          className="hv3-plan-ground sticky top-0 flex h-screen w-full flex-col overflow-hidden"
        >
          {/* Pre-blurred base. Slightly over-scaled so the blur's soft edge
              never exposes the container edge. */}
          <div className="absolute inset-0 scale-105" aria-hidden>
            <div
              className="absolute inset-0"
              style={{ filter: `blur(${CFG.maxBlur}px)` }}
            >
              <Image
                src={PLAN_BG}
                alt=""
                fill
                sizes="(min-width: 1024px) 2400px, 100vw"
                loading="eager"
                className="object-cover"
              />
            </div>
            {/* Sharp copy on top, cross-faded out over beat one. */}
            <div
              className="absolute inset-0"
              style={{ opacity: "var(--sharp, 1)", willChange: "opacity" }}
            >
              <Image
                src={PLAN_BG}
                alt=""
                fill
                sizes="(min-width: 1024px) 2400px, 100vw"
                loading="eager"
                className="object-cover"
              />
            </div>
          </div>
          {/* The scrim arrives with the blur, not before: beat one is meant to
              read as a photograph, beat two as a surface to put tiles on. */}
          <div
            className="absolute inset-0 bg-black"
            style={{
              opacity: `calc(var(--dark, 1) * ${CFG.darken})`,
              willChange: "opacity",
            }}
            aria-hidden
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(90deg, rgba(18, 15, 14, 0.38) 0%, rgba(18, 15, 14, 0.14) 42%, transparent 70%)",
            }}
            aria-hidden
          />

          {/* `w-full` is load-bearing: `SECTION` carries `mx-auto`, and auto
              margins on a flex item shrink it to its content and centre it —
              which silently moved the whole heading block to the middle of the
              stage. */}
          <div
            className={`${section} relative z-10 w-full shrink-0 pt-[104px]`}
          >
            <p className="type-label-lg text-neutral-300">{COPY.eyebrow}</p>
            <h2 className="type-display-md-emphasized mt-[10px] max-w-[386px] text-white">
              {COPY.heading.plain}
              <br />
              <span className="text-bright-green-400">
                {COPY.heading.accent}
              </span>
            </h2>
            {/* Figma sets this at 19.14/28.72 — a scaled group, not a rung.
                On the ladder that is body-xl. Fades out as the tiles arrive
                (`--lede`); the heading above it stays for the whole track.

                ABSOLUTE, not in flow: it belongs to beat one only, so the
                collage below is sized against a stage that never counted it.
                That is what lets the tiles sit higher and larger — they take
                the room the lede is about to vacate, rather than waiting for
                a gap that stays reserved all the way down the track. Nothing
                overlaps: while the lede is on screen the tiles are not. */}
            <p
              className="type-body-xl absolute mt-[46px] max-w-[340px] text-white"
              style={{ opacity: "var(--lede, 1)", willChange: "opacity" }}
            >
              {COPY.lede}
            </p>
          </div>

          {/* The collage keeps the Figma frame's 1512×687 proportions and is
              sized by the height left over in the pinned viewport, so the
              whole composition is always visible without a scrollbar. Tiles
              are placed as percentages of that box. */}
          <div className="relative z-10 flex min-h-0 flex-1 items-start justify-center pt-12 pb-10">
            {/* Sized by whichever runs out first: the design's own 1512, the
                viewport width, or the height left in the pinned stage (hence
                the header + padding constant). All three are lengths, so the
                min() is valid — mixing in a unitless ratio would drop the
                whole declaration. The aspect ratio then gives the height, so
                the composition never distorts and never overflows. */}
            <div
              className="hv3-collage relative"
              style={{
                width: `min(${BOX_W}px, 100%, calc((100vh - ${STAGE_HEADER}px) * ${BOX_W / BOX_H}))`,
                aspectRatio: `${BOX_W} / ${BOX_H}`,
              }}
            >
              {tiles}
              <ul
                className="hv3-plan-trust absolute flex items-center gap-9"
                style={{
                  left: `${(467 / BOX_W) * 100}%`,
                  top: `${(654 / BOX_H) * 100}%`,
                }}
              >
                {trustRow}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Under `lg` there is no pinning and no reveal: the tiles stack in the
          same order over the blurred photograph. A pinned track on a phone
          costs two extra viewports of scroll to say the same thing. */}
      <section className="hv3-plan-ground relative isolate overflow-hidden py-10 lg:hidden">
        <Image
          src={PLAN_BG}
          alt=""
          fill
          sizes="100vw"
          className="scale-105 object-cover blur-[9px]"
          aria-hidden
        />
        <div className="absolute inset-0 bg-black/45" aria-hidden />
        <div className={`${section} relative z-10`}>
          <p className="type-label-lg text-neutral-300">{COPY.eyebrow}</p>
          <h2 className="type-display-md-emphasized mt-[10px] max-w-[386px] text-white">
            {COPY.heading.plain}
            <br />
            <span className="text-bright-green-400">{COPY.heading.accent}</span>
          </h2>
          <p className="type-body-xl mt-6 max-w-[340px] text-white">
            {COPY.lede}
          </p>
          {/* Same layered tiles, stacked. Each wrapper IS its tile here, so
              one Figma pixel is the wrapper's width over the tile's own Figma
              width — not over the collage's 1512 as on desktop, which would
              draw every layer at a fifth of its size. `data-in` is static:
              nothing is pinned, so the contents are simply there. */}
          <div className="mt-10 flex flex-col gap-6">
            {COPY.tiles.map((tile) => {
              const Tile = PLAN_TILE_COMPONENTS[tile.kind]
              return (
                <div
                  key={tile.kind}
                  className="hv3-collage relative w-full max-w-[420px]"
                  style={
                    {
                      aspectRatio: "1 / 1",
                      "--px": `calc(100cqw / ${tile.w})`,
                    } as React.CSSProperties
                  }
                  data-in=""
                >
                  <Tile />
                </div>
              )
            })}
          </div>
          <ul className="mt-10 flex flex-col gap-4 opacity-90">{trustRow}</ul>
        </div>
      </section>
    </>
  )
}
